import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Aligned with the backend SEGMENT_RE (underscore allowed).
const SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;

interface FetchState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  html?: string;
  errorMessage?: string;
  errorStatus?: number;
}

export default function DeckView() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug || '';
  const { getIdToken, logout } = useAuth();
  const [state, setState] = useState<FetchState>({ status: 'idle' });

  const slugOk = SLUG_RE.test(slug);

  useEffect(() => {
    if (!slugOk) return;
    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      try {
        const token = await getIdToken();
        if (!token) throw new Error('Not signed in.');

        const res = await fetch(`/api/decks/${encodeURIComponent(slug)}`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'omit',
        });
        if (cancelled) return;
        if (!res.ok) {
          setState({
            status: 'error',
            errorStatus: res.status,
            errorMessage:
              res.status === 401 || res.status === 403
                ? 'Your session is not authorized for this deck.'
                : `Failed to load (HTTP ${res.status}).`,
          });
          return;
        }
        const html = await res.text();
        if (cancelled) return;
        setState({ status: 'ready', html });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load.';
        setState({ status: 'error', errorMessage: message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slugOk, slug, getIdToken]);

  if (!slugOk) return <Navigate to="/" replace />;

  return (
    <div className="deck-view">
      {state.status === 'loading' && (
        <div className="deck-view__status" aria-live="polite">
          Loading deck…
        </div>
      )}
      {state.status === 'error' && (
        <div className="deck-view__status deck-view__status--error" role="alert">
          <div className="deck-view__status-head">
            <AlertTriangle className="h-4 w-4" />
            <span>{state.errorStatus ? `Error ${state.errorStatus}` : 'Error'}</span>
          </div>
          <p>{state.errorMessage}</p>
          {(state.errorStatus === 401 || state.errorStatus === 403) && (
            <button onClick={logout} className="deck-view__signout">
              Sign out and try again
            </button>
          )}
        </div>
      )}
      {state.status === 'ready' && state.html && (
        // The deck is our own static HTML, but it ships inline JS. We render it in
        // an isolated iframe: `sandbox="allow-scripts"` WITHOUT allow-same-origin
        // gives it an opaque origin, so its scripts run but it CANNOT read the
        // parent origin's Firebase token / localStorage. Fullscreen is granted via
        // the `allow="fullscreen"` Permissions-Policy (there is NO allow-fullscreen
        // sandbox token — the browser rejects it). A <meta> CSP inside the deck
        // HTML confines it further.
        <iframe
          title="Prospect deck"
          srcDoc={state.html}
          sandbox="allow-scripts"
          allow="fullscreen"
          referrerPolicy="no-referrer"
          className="deck-view__frame"
        />
      )}
    </div>
  );
}
